'use client';
import { useState, useMemo } from 'react';
import { MoreVerticalIcon } from 'lucide-react';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { IconButton, Menu, MenuItem } from '@mui/material';
import {
  useDeletePromptMutation,
  useGetPromptHistoryQuery,
  useUpdatePromptMutation,
  useUpdatePromptStatusMutation,
} from '@/store/api/promptHistoryApi';
import { toast } from 'react-toastify';

const folderCategories = [
  { label: 'All', value: 'all' },
  { label: 'Tech Data', value: 'tech-data' },
  { label: 'Semi Tech', value: 'semi-tech' },
  { label: 'Non Tech', value: 'non-tech' },
];
export default function PromptHistory() {
  const [folder, setFolder] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [openEdit, setOpenEdit] = useState(false);
  const [editPrompt, setEditPrompt] = useState('');

  const [updatePrompt] = useUpdatePromptMutation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 7,
  });
  const [openDelete, setOpenDelete] = useState(false);
  const { data, isLoading } = useGetPromptHistoryQuery({
    category: folder,
    fromDate,
    toDate,
    skip: paginationModel.page * paginationModel.pageSize,
    take: paginationModel.pageSize,
  });
  const [openView, setOpenView] = useState(false);
  const [viewPrompt, setViewPrompt] = useState('');
  const [updateStatus] = useUpdatePromptStatusMutation();
  const [deleteFile] = useDeletePromptMutation();
  const processedFiles = useMemo(() => {
    if (!data?.data) return [];
    return data.data;
  }, [data]);

  const handleMenuOpen = (event: any, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    // setSelectedRow(null);
  };

  const handleToggleStatus = async () => {
    const newStatus = selectedRow.status === 'Active' ? 'Inactive' : 'Active';

    await updateStatus({
      id: selectedRow.id,
      status: newStatus,
    });
    toast.success(`Status updated to ${newStatus}`);
    handleMenuClose();
  };

  // const handleDelete = async () => {
  //   await deleteFile(selectedRow.id);
  //   toast.success('Prompt deleted successfully');
  //   handleMenuClose();
  // };
  const handleDelete = async () => {
    if (!selectedRow) return;

    try {
      await deleteFile(selectedRow.id).unwrap();
      toast.success('Prompt deleted successfully');
    } catch (error) {
      toast.error('Delete failed');
    }

    setOpenDelete(false);
  };
  const rows = processedFiles.map((file, index) => ({
    id: file.id,
    serial: paginationModel.page * paginationModel.pageSize + index + 1,
    name: file.prompt,
    status: file.PromptStatus,
    createdAt: file.createdAt,
  }));

  const columns: GridColDef[] = [
    { field: 'serial', headerName: 'S.No', width: 80 },

    {
      field: 'name',
      headerName: 'Prompt Name',
      flex: 1,
      renderCell: (params) => (
        <span
          onClick={() => handleViewOpen(params.row)}
          style={{
            cursor: 'pointer',
            color: '#000',
            textDecoration: 'underline',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
            maxWidth: '80%',
          }}
        >
          {params.row.name}
        </span>
      ),
    },

    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (params) => {
        const isActive = params.row.status === 'Active';

        return (
          <span
            style={{
              padding: '4px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: 500,
              backgroundColor: isActive ? '#e6f4ea' : '#fdecea',
              color: isActive ? '#2e7d32' : '#d32f2f',
            }}
          >
            {params.row.status}
          </span>
        );
      },
    },

    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => {
        const date = new Date(params.row.createdAt);
        return (
          <span style={{ fontSize: '12px' }}>
            {date.toLocaleDateString()} {date.toLocaleTimeString()}
          </span>
        );
      },
    },

    {
      field: 'action',
      headerName: 'Action',
      width: 80,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuOpen(e, params.row)}>
          <MoreVerticalIcon />
        </IconButton>
      ),
    },
  ];
  const handleEditOpen = () => {
    if (!selectedRow) return;
    setEditPrompt(selectedRow.name);
    setOpenEdit(true);
    handleMenuClose();
  };
  const handleEditSubmit = async () => {
    if (!editPrompt.trim()) return;
    await updatePrompt({
      id: selectedRow.id,
      prompt: editPrompt,
    });

    setOpenEdit(false);
  };
  const handleViewOpen = (row: any) => {
    setViewPrompt(row.name);
    setOpenView(true);
  };
  return (
    <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Prompt History</h1>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        {/* <select
          value={folder}
          onChange={(e) => setFolder(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        >
          {folderCategories.map((cat) => (
            <option key={cat.value} value={cat.value}>
              {cat.label}
            </option>
          ))}
        </select> */}

        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        />

        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="text-xs px-3 py-2 border rounded-lg"
        />
      </div>

      {/* Table */}
      <div style={{ height: 500, width: '100%' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          rowCount={data?.count || 0}
          paginationMode="server"
          paginationModel={paginationModel}
          pageSizeOptions={[7, 10, 20]}
          onPaginationModelChange={setPaginationModel}
        />

        {/* Action Menu */}
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={handleEditOpen}>Edit</MenuItem>
          <MenuItem onClick={handleToggleStatus}>
            {selectedRow?.status === 'Active' ? ' Inactive' : ' Active'}
          </MenuItem>

          <MenuItem
            onClick={() => {
              setOpenDelete(true);
              handleMenuClose();
            }}
            style={{ color: 'red' }}
          >
            Delete
          </MenuItem>
        </Menu>
      </div>
      {openEdit && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded-lg w-[600px] space-y-4">
            <h2 className="text-lg font-semibold">Edit Prompt</h2>

            <textarea
              value={editPrompt}
              onChange={(e) => setEditPrompt(e.target.value)}
              rows={5}
              className="w-full border rounded-md px-3 py-2 text-sm"
            />

            <div className="flex justify-end gap-2">
              <button onClick={() => setOpenEdit(false)} className="px-3 py-1 border rounded">
                Cancel
              </button>

              <button onClick={handleEditSubmit} className="px-3 py-1 bg-black text-white rounded">
                Save
              </button>
            </div>
          </div>
        </div>
      )}
      {openView && (
        <div
          onClick={() => setOpenView(false)}
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white p-5 rounded-lg w-[500px] max-h-[80vh] overflow-auto space-y-4"
          >
            <h2 className="text-lg font-semibold">View Prompt</h2>

            <div className="border p-3 rounded text-sm whitespace-pre-wrap">{viewPrompt}</div>

            <div className="flex justify-end">
              <button onClick={() => setOpenView(false)} className="px-3 py-1 border rounded">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {openDelete && (
        <div
          className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
          onClick={() => setOpenDelete(false)}
        >
          <div
            className="bg-white p-5 rounded-lg w-[350px] space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-lg font-semibold text-red-600">Confirm Delete</h2>

            <p className="text-sm text-gray-600">Are you sure you want to delete this prompt?</p>

            <div className="flex justify-end gap-2">
              <button onClick={() => setOpenDelete(false)} className="px-3 py-1 border rounded">
                Cancel
              </button>

              <button onClick={handleDelete} className="px-3 py-1 bg-red-600 text-white rounded">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      {isLoading && <p className="text-sm text-gray-500 mt-3">Loading...</p>}
    </div>
  );
}
